import {useEffect, useMemo, useState} from 'react';
import type {DebouncedFunc} from 'lodash';
import {debounce} from 'lodash';
import {emailRegex} from '@constants/regex';

type Validator = (email: string) => boolean;
type CancelableValidator = DebouncedFunc<Validator>;

const isCancelable = (
	validator: Validator | CancelableValidator,
): validator is CancelableValidator => {
	return 'cancel' in validator;
};

export const useEmailValidator = (
	delay?: number,
): [boolean, Validator | CancelableValidator] => {
	const [hasError, setHasError] = useState(false);

	const validateEmail: Validator | CancelableValidator = useMemo(() => {
		const validator: Validator = (email: string): boolean => {
			const hasEmailError = !emailRegex.test(email);

			setHasError(hasEmailError);

			return hasEmailError;
		};

		if (delay) {
			return debounce(validator, delay);
		}

		return validator;
	}, [delay]);

	useEffect(() => {
		return () => {
			if (isCancelable(validateEmail)) {
				validateEmail.cancel();
			}
		};
	}, [validateEmail]);

	return [hasError, validateEmail];
};

export default useEmailValidator;
